import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProcessStep } from '@/components/landing/ProcessStep';

import { LandingHeader } from '@/components/layout/LandingHeader';
import { useAuthStore } from '@/store/authStore';
import { useEffect, useState } from 'react';
import { userService, UserProgress } from '@/services/userService';
import WistiaVideo from '@/components/video/WistiaVideo';
import { useMonitoring } from '@/hooks/useMonitoring';
import SEO from '@/components/common/SEO';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { trackEvent, trackClarityEvent, trackRedditEvent, trackMetaEvent, trackXEvent } = useMonitoring();
  const { isAuthenticated } = useAuthStore();
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [purchasePrice, setPurchasePrice] = useState<number | null>(null);
  const [fetchedStep, setFetchedStep] = useState<number | null>(null);
  const [calculatorAmount, setCalculatorAmount] = useState(5000);
  const [calculatorEmail, setCalculatorEmail] = useState('');
  const [selectedFaqVideo, setSelectedFaqVideo] = useState(0);

  // FAQ Video data
  const faqVideos = [
    { id: 'legitimitaet', wistiaId: 'k6z7dfp5kp', titleKey: 'home.faqVideos.legitimitaet.title', descKey: 'home.faqVideos.legitimitaet.shortDesc' },
    { id: 'unterschied', wistiaId: 'om9rzoqul8', titleKey: 'home.faqVideos.unterschied.title', descKey: 'home.faqVideos.unterschied.shortDesc' },
    { id: 'schadensersatz', wistiaId: '0e2dnxrzh3', titleKey: 'home.faqVideos.schadensersatz.title', descKey: 'home.faqVideos.schadensersatz.shortDesc' },
    { id: 'datenschutz', wistiaId: '807whfyobf', titleKey: 'home.faqVideos.datenschutz.title', descKey: 'home.faqVideos.datenschutz.shortDesc' },
    { id: 'sperrung', wistiaId: 'bvnx73hk1d', titleKey: 'home.faqVideos.sperrung.title', descKey: 'home.faqVideos.sperrung.shortDesc' },
  ];

  useEffect(() => {
    if (isAuthenticated) {
      userService.getProgress()
        .then(response => {
          if (response.success && response.progress) {
            setUserProgress(response.progress);
            setFetchedStep(response.progress.currentStep);
            if (typeof response.purchasePrice === 'number') {
              setPurchasePrice(response.purchasePrice);
            }
          }
        })
        .catch(error => {
          console.error('Failed to fetch user progress:', error);
          // Don't crash - just leave progress as null
        });
    }
  }, [isAuthenticated]);

  // Compute the current step from fetched data
  const currentStep = fetchedStep ?? 0;

  const navigateToLogin = () => {
    // Track lead events (don't block navigation if tracking fails)
    try {
      trackRedditEvent('Lead');
      trackMetaEvent('Lead');
      trackXEvent('tw-qy0lg-qy0lh'); // X Lead event
    } catch (e) {
      console.error('Lead tracking failed:', e);
    }
    navigate(`/${i18n.language}/auth/login`);
  };

  const getHeroAction = () => {
    // Not authenticated - go to login
    if (!isAuthenticated) {
      const cachedEligibility = localStorage.getItem('eligibilityData');
      if (cachedEligibility) {
        return {
          text: t('home.cta_start', 'Jetzt starten'),
          action: navigateToLogin
        };
      }
      return {
        text: t('home.cta_check_eligibility', 'Jetzt Anspruch einreichen'),
        action: navigateToLogin
      };
    }

    // Authenticated - route based on progress (skip popup)
    if (!userProgress) {
      // Still loading progress
      return {
        text: t('home.hero.cta_submit', 'Jetzt Anspruch einreichen'),
        action: () => {} // Do nothing while loading
      };
    }

    const { completedAt } = userProgress;

    // Signed contract - go to completed page
    if (completedAt?.contractCompleted) {
      return {
        text: t('home.cta_view_status', 'Status ansehen'),
        action: () => navigate(`/${i18n.language}/docusign/complete`)
      };
    }

    // Has banking - go to sign contract
    if (completedAt?.bankingProvided) {
      return {
        text: t('home.steps.step4.title', 'Identität verifizieren'),
        action: () => navigate(`/${i18n.language}/docusign/verify`)
      };
    }

    // Has documents AND IS ELIGIBLE - go to banking
    // If purchasePrice is 0 (below threshold), they should stay on documents page
    const isEligible = (purchasePrice !== null && purchasePrice > 0);
    
    if (completedAt?.documentsUploaded && isEligible) {
      return {
        text: t('banking.iban.title', 'Bankverbindung'),
        action: () => navigate(`/${i18n.language}/banking/iban`)
      };
    }

    // Default - go to documents
    return {
      text: t('home.hero.cta_submit', 'Jetzt Anspruch einreichen'),
      action: () => navigate(`/${i18n.language}/documents`)
    };
  };

  const heroAction = getHeroAction();

  const processSteps = [
    {
      icon: <span className="text-5xl">📝</span>,
      title: t('home.process_new.evaluate.title', 'Bewerten'),
      description: t('home.process_new.evaluate.desc', 'Online‑App (2–5 Min)')
    },
    {
      icon: <span className="text-5xl">📩</span>,
      title: t('home.process_new.offer.title', 'Angebot'),
      description: t('home.process_new.offer.desc', 'Verbindliches Angebot von FWC')
    },
    {
      icon: <span className="text-5xl">✍️</span>,
      title: t('home.process_new.sell.title', 'Verkaufen'),
      description: t('home.process_new.sell.desc', 'Digitaler Kaufvertrag mit FWC')
    },
    {
      icon: <span className="text-5xl">🎉</span>,
      title: t('home.process_new.receive.title', 'Geld erhalten'),
      description: t('home.process_new.receive.desc', 'Schnelle Auszahlung innerhalb weniger Tage')
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title={t('home.hero.main_title', 'Holen Sie sich zurück,')}
        description={t('home.seo_description', 'Jetzt Angebotspreis prüfen – schnell, unkompliziert und ohne Risiko.')}
      />
      {/* Landing page header with navigation */}
      <LandingHeader />
      <main id="main-content" tabIndex={-1} className="focus:outline-hidden">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-linear-to-br from-primary-50 to-[rgb(var(--background))] dark:from-[rgb(var(--background))] dark:to-[rgb(var(--background-secondary))] pt-16">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        {/* Animated background effects for dark mode */}
        <div className="absolute inset-0 dark:block hidden">
          <div className="absolute top-20 left-1/4 w-72 h-72 bg-purple-700 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
          <div className="absolute top-40 right-1/4 w-96 h-96 bg-blue-700 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-cyan-700 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8">
          {/* Full width header */}
          <div className="text-center mb-6">
            <h1 className="hero-title text-4xl font-display font-bold tracking-tight text-[rgb(var(--foreground))] sm:text-5xl md:text-6xl lg:text-7xl">
              {t('home.hero.main_title', 'Holen Sie sich zurück,')}<br/>
              {t('home.hero.main_title_line2', 'was Ihnen zusteht.')}
            </h1>
            <h2 className="hero-subtitle mt-4 text-2xl sm:text-3xl text-green-600 dark:text-green-500 font-semibold">
              {t('home.hero.subtitle', 'Wir kaufen Ihren Anspruch gegen Amazon für Einkäufe')}<br/>
              {t('home.hero.subtitle_line2', 'aus den Jahren 2017-2022 – schnell & unkompliziert.')}
            </h2>
          </div>
          
          {/* Hero Video */}
          <div className="hero-video flex justify-center mb-10">
            <WistiaVideo
              mediaId="l5eleu09lr"
              className="w-full max-w-3xl rounded-xl shadow-lg overflow-hidden"
            />
          </div>
          
          {/* Buttons and text section - Standard V2 Layout */}
          <div className="hero-content max-w-4xl mx-auto text-center grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Left: Former headline */}
            <div className="order-1 md:col-span-1 text-center md:text-left md:border-r md:border-gray-300 dark:md:border-gray-600 md:pr-8">
              <h3 className="text-2xl sm:text-3xl font-bold text-[rgb(var(--foreground))] mb-2">
                {t('home.hero.body_headline', 'Verkaufen Sie Ihre Amazon-Ansprüche')}
              </h3>
              <p className="text-xl text-green-600 dark:text-green-500 font-semibold">
                {t('home.hero.content_subtitle', 'Zahlung i.d.R. innerhalb von ca. 7 Tagen. Keine Kosten, kein Risiko.')}
              </p>
            </div>

            {/* Buttons - Moved higher on mobile (Order 2) */}
            <div className="order-2 md:order-3 md:col-span-2 hero-buttons flex flex-col sm:flex-row gap-4 justify-center w-full mb-4 md:mb-8">
              <button
                onClick={() => {
                  trackEvent('hero_cta_clicked', { action: heroAction.text });
                  trackClarityEvent('hero_cta_clicked');
                  heroAction.action();
                }}
                className="cta-button inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 dark:bg-linear-to-r dark:from-[rgb(var(--primary-gradient-from))] dark:to-[rgb(var(--primary-gradient-to))] focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))] transition-all duration-200 transform hover:scale-105 shadow-lg cursor-pointer"
                aria-label={heroAction.text}
              >
                {heroAction.text}
                <svg className="ml-2 -mr-1 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <button
                onClick={() => {
                  const element = document.getElementById('how-it-works');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-lg text-primary-600 underline hover:text-primary-700 transition-all duration-200 cursor-pointer"
                aria-label={t('home.hero.cta_learn', 'So funktioniert\'s')}
              >
                {t('home.hero.cta_learn', 'So funktioniert\'s')}
              </button>
            </div>

            {/* Right: Marketing intro - Pushed down on mobile (Order 3) */}
            <div className="order-3 md:order-2 md:col-span-1 text-center md:text-left">
              <p className="text-lg text-[rgb(var(--foreground))] leading-relaxed">
                {t('home.hero.marketing_intro', 'Jetzt Angebotspreis prüfen – schnell, unkompliziert und ohne Risiko. Die EU-Kommission hat festgestellt, dass Amazons Verhalten zwischen 2017 und 2022 wettbewerbsrechtliche Bedenken aufwarf.')}
              </p>
            </div>

            {/* Trustpilot Widget (Order 4) */}
           
              <div
                className="trustpilot-widget order-4 md:col-span-2 flex justify-center"
                data-locale="de-DE"
                data-template-id="53aa8807dec7e10d38f59f32"
                data-businessunit-id="693bcbf38e89501ab005c313"
                data-style-height="150px"
                data-style-width="100%"
                data-theme="light"
                data-token="d256a2c6-c945-418c-9117-b58261f1f7e3"
              >
                
            </div>

            {/* Blue Info Card (Order 5) */}
            <div className="order-5 md:col-span-2 mb-8 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 text-left">
              <div className="flex items-start gap-4">
                <span className="text-2xl flex-shrink-0">ℹ️</span>
                <p className="text-lg text-[rgb(var(--foreground))] leading-relaxed">
                   <span dangerouslySetInnerHTML={{ __html: t('home.hero.info_card', 'Für <strong>Verbraucherinnen und Verbraucher in Deutschland</strong> bedeutet das: Ihre <strong>Amazon-Einkäufe aus dem Zeitraum 2017 bis 2022</strong> könnten zu finanziellen Nachteilen geführt haben. Wenn Sie in dieser Zeit bei Amazon.de bestellt haben, können Sie <strong>Anspruch auf eine Auszahlung</strong> haben.') }} />
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Trust Indicators */}
        <div className="relative bg-white dark:bg-[rgb(var(--background-secondary))] py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-gray-50 dark:bg-[rgb(var(--card))] p-8 rounded-xl shadow-md border border-transparent dark:border-[rgb(var(--border))]">
                <div className="text-5xl mb-3">💶</div>
                <h3 className="text-xl font-bold mb-1 text-[rgb(var(--foreground))]">{t('home.trust_new.percentage', '15% bis 20% des erwarteten Schadens')}</h3>
                <p className="text-lg text-[rgb(var(--foreground-muted))]">{t('home.trust_new.percentage_desc', 'Richtwert in Bezug auf berechtigten Umsatz')}</p>
              </div>
              <div className="bg-gray-50 dark:bg-[rgb(var(--card))] p-8 rounded-xl shadow-md border border-transparent dark:border-[rgb(var(--border))]">
                <div className="text-5xl mb-3">📆</div>
                <h3 className="text-xl font-bold mb-1 text-[rgb(var(--foreground))]">{t('home.trust_new.avg_days', 'Ø ≤ 7 Tage')}</h3>
                <p className="text-lg text-[rgb(var(--foreground-muted))]">{t('home.trust_new.avg_days_desc', 'Zeit bis zur Auszahlung (nach erfolgreichem Identifikations-Verfahren)')}</p>
              </div>
              <div className="bg-gray-50 dark:bg-[rgb(var(--card))] p-8 rounded-xl shadow-md border border-transparent dark:border-[rgb(var(--border))]">
                <div className="text-5xl mb-3">🛡️</div>
                <h3 className="text-xl font-bold mb-1 text-[rgb(var(--foreground))]">{t('home.trust_new.zero_risk', 'Null Risiko')}</h3>
                <p className="text-lg text-[rgb(var(--foreground-muted))]">{t('home.trust_new.zero_risk_desc', 'FWC übernimmt Anspruch und Risiko - Sie sind raus')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-8 md:py-14 px-4 md:px-6 bg-gray-100 dark:bg-[rgb(var(--background))]" id="calculator">
        <h2 className="text-2xl md:text-3xl font-semibold mb-4 md:mb-8 text-center text-[rgb(var(--foreground))]">
          {t('home.calculator.title', 'Was ist Ihr Anspruch heute wert?')}
        </h2>

        {/* Mobile Compact Calculator */}
        <div className="md:hidden max-w-lg mx-auto">
          <div className="bg-white dark:bg-[rgb(var(--card))] p-4 rounded-xl shadow-md border border-transparent dark:border-[rgb(var(--border))]">
            {/* Slider */}
            <label htmlFor="amount-mobile" className="block mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('home.calculator.turnover_label', 'Amazon‑Umsatz 2017–2022 (€)')}
            </label>
            <input
              type="range"
              id="amount-mobile"
              min="2000"
              max="50000"
              step="100"
              value={calculatorAmount}
              onChange={(e) => setCalculatorAmount(Number(e.target.value))}
              className="w-full accent-blue-600 dark:accent-blue-500"
              aria-label={t('home.calculator.turnover_label', 'Amazon‑Umsatz 2017–2022 (€)')}
            />
            <div className="text-center text-primary-600 dark:text-primary-400 text-2xl font-bold mb-3">
              {calculatorAmount.toLocaleString('de-DE')} €
            </div>

            {/* Compact Results - Side by Side */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 text-center">
                <div className="text-xs font-medium text-blue-700 dark:text-blue-300">
                  {t('home.calculator.estimated_damage', 'Geschätzter Schaden')}
                </div>
                <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  {Math.round(calculatorAmount * 0.0291).toLocaleString('de-DE')} €
                </div>
              </div>
              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-center">
                <div className="text-xs font-medium text-green-700 dark:text-green-300">
                  {t('home.calculator.your_payout', 'Ihre Auszahlung')}
                </div>
                <div className="text-xl font-bold text-green-600 dark:text-green-400">
                  {Math.round(calculatorAmount * 0.005).toLocaleString('de-DE')} €
                </div>
              </div>
            </div>

            {/* Compact Benefits */}
            <div className="flex justify-center gap-4 text-sm mb-3">
              <span className="text-[rgb(var(--foreground))]">✓ {t('home.calculator.benefits.free', 'Kostenlos')}</span>
              <span className="text-[rgb(var(--foreground))]">✓ {t('home.calculator.benefits.fast', 'In 24h')}</span>
              <span className="text-[rgb(var(--foreground))]">✓ {t('home.calculator.benefits.non_binding', 'Unverbindlich')}</span>
            </div>

            {!isAuthenticated && (
              <button
                onClick={() => {
                  trackEvent('calculator_conversion', { amount: calculatorAmount });
                  trackClarityEvent('calculator_conversion');
                  navigateToLogin();
                }}
                className="bg-primary-600 hover:bg-primary-700 text-white w-full px-4 py-3 rounded text-lg font-semibold shadow transition-colors cursor-pointer"
              >
                {t('home.cta_check_eligibility', 'Jetzt Anspruch einreichen')}
              </button>
            )}
          </div>
        </div>

        {/* Desktop Calculator (original layout) */}
        <div className="hidden md:grid max-w-6xl mx-auto lg:grid-cols-2 gap-10">
          <div className="bg-white dark:bg-[rgb(var(--card))] p-8 rounded-xl shadow-md border border-transparent dark:border-[rgb(var(--border))]">
            <label htmlFor="amount" className="block mb-2 text-xl font-semibold text-gray-700 dark:text-gray-300">
              {t('home.calculator.turnover_label', 'Amazon‑Umsatz 2017–2022 (€)')}
            </label>
            <input
              type="range"
              id="amount"
              min="2000"
              max="50000"
              step="100"
              value={calculatorAmount}
              onChange={(e) => setCalculatorAmount(Number(e.target.value))}
              className="w-full accent-blue-600 dark:accent-blue-500"
              aria-label={t('home.calculator.turnover_label', 'Amazon‑Umsatz 2017–2022 (€)')}
            />
            <div className="text-center mt-3 text-primary-600 dark:text-primary-400 text-3xl font-bold">
              {calculatorAmount.toLocaleString('de-DE')} €
            </div>
            {!isAuthenticated && (
              <>
                <label className="block mt-6 mb-2 text-xl font-semibold text-gray-700 dark:text-gray-300">
                  {t('home.calculator.email_label', 'E‑Mail für Bewertung')}
                </label>
                <input
                  type="email"
                  value={calculatorEmail}
                  onChange={(e) => setCalculatorEmail(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded p-3 bg-white dark:bg-[rgb(var(--background))] text-[rgb(var(--foreground))]"
                  placeholder={t('home.calculator.email_placeholder', 'Ihre E‑Mail')}
                />
                <button
                  onClick={() => {
                    trackEvent('calculator_conversion', { amount: calculatorAmount });
                    trackClarityEvent('calculator_conversion');
                    navigateToLogin();
                  }}
                  className="bg-primary-600 hover:bg-primary-700 text-white w-full mt-6 px-5 py-3 rounded text-xl shadow transition-colors cursor-pointer"
                >
                  {t('home.cta_check_eligibility', 'Jetzt Anspruch einreichen')}
                </button>
              </>
            )}
          </div>

          <div className="bg-white dark:bg-[rgb(var(--card))] p-8 rounded-xl shadow-md border border-transparent dark:border-[rgb(var(--border))]">
            <h3 className="text-xl font-semibold mb-6 text-[rgb(var(--foreground))]">
              {t('home.calculator.estimated_price', 'Ihr geschätzter Ankaufspreis')}
            </h3>

            {/* Estimated Damage */}
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="text-base font-medium text-blue-700 dark:text-blue-300 mb-2">
                {t('home.calculator.estimated_damage', 'Geschätzter Schaden')}
              </div>
              <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                {Math.round(calculatorAmount * 0.0291).toLocaleString('de-DE')} €
              </div>
            </div>

            {/* Payout */}
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-base font-medium text-green-700 dark:text-green-300 mb-2">
                {t('home.calculator.your_payout', 'Ihre Auszahlung')}
              </div>
              <div className="text-4xl font-bold text-green-600 dark:text-green-400">
                {Math.round(calculatorAmount * 0.005).toLocaleString('de-DE')} €
              </div>
            </div>

            <p className="text-lg text-[rgb(var(--foreground-muted))] mb-3">
              {t('home.calculator.calculation_disclaimer', 'Berechnung auf Basis von Richtwerten. Finaler Ankaufspreis durch FWC nach Prüfung.')}
            </p>

            <div className="flex flex-col sm:flex-row sm:justify-between gap-4 sm:gap-0 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <span className="text-primary-600 dark:text-primary-400 text-2xl mr-2">✓</span>
                <span className="text-lg text-[rgb(var(--foreground))]">{t('home.calculator.benefits.free', 'Kostenlos')}</span>
              </div>
              <div className="flex items-center">
                <span className="text-primary-600 dark:text-primary-400 text-2xl mr-2">✓</span>
                <span className="text-lg text-[rgb(var(--foreground))]">{t('home.calculator.benefits.fast', 'In 24h')}</span>
              </div>
              <div className="flex items-center">
                <span className="text-primary-600 dark:text-primary-400 text-2xl mr-2">✓</span>
                <span className="text-lg text-[rgb(var(--foreground))]">{t('home.calculator.benefits.non_binding', 'Unverbindlich')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4-Step Process Section */}
      <section id="how-it-works" className="py-16 sm:py-24 bg-[rgb(var(--background-secondary))] dark:bg-[rgb(var(--background))]" aria-labelledby="process-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 id="process-title" className="text-3xl sm:text-4xl font-bold text-[rgb(var(--foreground))] dark:gradient-text">
              {t('home.process.title', 'Wie es funktioniert')}
            </h2>
            <p className="mt-4 text-xl text-[rgb(var(--foreground-muted))]">
              {t('home.process.subtitle', 'In nur 4 einfachen Schritten zu Ihrem Geld')}
            </p>
          </div>

          {/* Process Steps */}
          <div className="relative">
            {/* Horizontal line (hidden on mobile) */}
            <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-1 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 rounded-full" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
              {processSteps.map((step, index) => (
                <div key={index}>
                  <ProcessStep
                    number={index + 1}
                    title={step.title}
                    description={step.description}
                    icon={step.icon}
                    isActive={currentStep === index + 1}
                    isCompleted={(() => {
                      if (!userProgress) return false;
                      const { completedAt } = userProgress;
                      switch (index) {
                        case 0: return !!completedAt?.accountCreated; // Evaluate - account created
                        case 1: return !!completedAt?.documentsUploaded; // Offer - documents uploaded
                        case 2: return !!completedAt?.contractCompleted; // Sell - contract signed
                        case 3: return !!completedAt?.payoutExecuted; // Receive - payment executed
                        default: return false;
                      }
                    })()}
                    showConnectorLines={false}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <div className="mt-12 text-center">
            <button
              onClick={() => {
                trackEvent('process_cta_clicked', { authenticated: isAuthenticated });
                trackClarityEvent('process_cta_clicked');
                heroAction.action();
              }}
              className="inline-flex items-center px-8 py-4 text-xl font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 dark:bg-linear-to-r dark:from-[rgb(var(--primary-gradient-from))] dark:to-[rgb(var(--primary-gradient-to))] focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ring))] transition-all duration-200 transform hover:scale-105 dark:btn-gradient cursor-pointer"
            >
              {isAuthenticated ? heroAction.text : t('home.cta_start', 'Jetzt starten')}
              <svg className="ml-2 -mr-1 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Videos Section */}
      <section id="faq-videos" className="py-16 sm:py-24 bg-white dark:bg-[rgb(var(--background-secondary))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block py-2 px-4 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-base font-medium mb-5 border border-purple-200 dark:border-purple-700">
              {t('home.faqVideos.label')}
            </span>
            <h2 className="section-title text-3xl sm:text-4xl font-bold text-[rgb(var(--foreground))] dark:gradient-text mb-4">
              {t('home.faqVideos.title')}
            </h2>
            <p className="text-xl text-[rgb(var(--foreground-muted))] max-w-3xl mx-auto">
              {t('home.faqVideos.subtitle')}
            </p>
          </div>

          <div className="flex flex-col-reverse gap-8 lg:grid lg:grid-cols-3">
            {/* Video Tabs - Left Side */}
            <div className="lg:col-span-1 space-y-3">
              {faqVideos.map((video, index) => (
                <button
                  key={video.id}
                  onClick={() => setSelectedFaqVideo(index)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-300 cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
                    selectedFaqVideo === index
                      ? 'bg-primary-700 text-white shadow-lg scale-[1.02]'
                      : 'bg-gray-50 dark:bg-[rgb(var(--card))] text-[rgb(var(--foreground))] hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-[rgb(var(--border))]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedFaqVideo === index
                        ? 'bg-white/20'
                        : 'bg-primary-100 dark:bg-primary-900/30'
                    }`}>
                      <svg className={`w-5 h-5 ${selectedFaqVideo === index ? 'text-white' : 'text-primary-600 dark:text-primary-400'}`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold text-sm truncate ${
                        selectedFaqVideo === index ? 'text-white' : 'text-[rgb(var(--foreground))]'
                      }`}>
                        {t(video.titleKey)}
                      </h3>
                      <p className={`text-xs mt-0.5 truncate ${
                        selectedFaqVideo === index ? 'text-white' : 'text-[rgb(var(--foreground-muted))]'
                      }`}>
                        {t(video.descKey)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Video Player - Right Side */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 dark:bg-[rgb(var(--card))] rounded-2xl p-4 border border-gray-200 dark:border-[rgb(var(--border))] shadow-lg">
                <WistiaVideo
                  mediaId={faqVideos[selectedFaqVideo].wistiaId}
                  className="w-full rounded-xl overflow-hidden"
                />
                <div className="mt-4 px-2">
                  <h3 className="text-xl font-bold text-[rgb(var(--foreground))]">
                    {t(faqVideos[selectedFaqVideo].titleKey)}
                  </h3>
                  <p className="mt-2 text-[rgb(var(--foreground-muted))]">
                    {t(faqVideos[selectedFaqVideo].descKey)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - Why Sell Instead of Sue */}
      <section id="case-details" className="py-16 sm:py-24 bg-white dark:bg-[rgb(var(--background-secondary))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[rgb(var(--foreground))] mb-12">
            {t('home.comparison.title', 'Warum verkaufen statt klagen?')}
          </h2>
          
          {/* Comparison - Selber Klagen vs Verkauf an FWC */}
          <div className="relative mb-12">
            {/* Headers with Versus in between */}
            <div className="flex items-center justify-center gap-4 md:gap-8 mb-8">
              <div className="flex items-center gap-3">
                <span className="text-4xl md:text-6xl">⏳</span>
                <h3 className="text-xl md:text-3xl font-black text-red-700 dark:text-red-400">{t('home.comparison.sue_yourself', 'Selber Klagen')}</h3>
              </div>
              <span className="text-lg md:text-2xl font-black text-gray-500 dark:text-gray-400">Versus</span>
              <div className="flex items-center gap-3">
                <h3 className="text-xl md:text-3xl font-black text-green-700 dark:text-green-400">{t('home.comparison.fwc_way', 'Verkauf an FWC')}</h3>
                <span className="text-4xl md:text-6xl">🚀</span>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4 lg:gap-8">
              {/* Traditional Way */}
              <div className="bg-gradient-to-br from-red-100 to-red-50 dark:from-red-900/30 dark:to-red-900/10 p-6 md:p-8 rounded-2xl">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-red-500 text-xl font-bold">✗</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.traditional_points.0', '5+ Jahre auf Gerichtsurteil warten')}</span>
                  </li>
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-red-500 text-xl font-bold">✗</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.traditional_points.1', 'Hohe Anwalts- und Gerichtskosten')}</span>
                  </li>
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-red-500 text-xl font-bold">✗</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.traditional_points.2', 'Ungewisser Ausgang')}</span>
                  </li>
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-red-500 text-xl font-bold">✗</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.traditional_points.3', 'Komplexe Gerichtsverfahren')}</span>
                  </li>
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-red-500 text-xl font-bold">✗</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.traditional_points.4', 'Keine Garantie auf Entschädigung')}</span>
                  </li>
                </ul>
              </div>

              {/* Sale to FWC */}
              <div className="bg-gradient-to-br from-green-100 to-green-50 dark:from-green-900/30 dark:to-green-900/10 p-6 md:p-8 rounded-2xl">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-green-500 text-xl font-bold">✓</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.fwc_points.0', 'Geld i. d. R. innerhalb von 7 Tagen')}</span>
                  </li>
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-green-500 text-xl font-bold">✓</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.fwc_points.1', 'Keine Kosten, kein Risiko für Sie')}</span>
                  </li>
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-green-500 text-xl font-bold">✓</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.fwc_points.2', 'Garantierte Auszahlung')}</span>
                  </li>
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-green-500 text-xl font-bold">✓</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.fwc_points.3', 'Einfacher Online-Prozess')}</span>
                  </li>
                  <li className="flex items-start gap-3 bg-white/50 dark:bg-black/20 rounded-lg p-3">
                    <span className="text-green-500 text-xl font-bold">✓</span>
                    <span className="text-lg text-gray-700 dark:text-gray-300">{t('home.comparison.fwc_points.4', 'FWC übernimmt alles')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 bg-[rgb(var(--background))] dark:bg-[rgb(var(--background))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="sr-only">Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mx-auto group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="mt-6 text-xl font-bold text-neutral-900 dark:text-white">
                {t('home.feature1.title', 'Einfach & Schnell')}
              </h3>
              <p className="mt-2 text-lg text-neutral-600 dark:text-neutral-400">
                {t('home.feature1.desc', 'Kompletter Prozess in wenigen Schritten abgeschlossen.')}
              </p>
            </div>

            <div className="text-center group">
              <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mx-auto group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="mt-6 text-xl font-bold text-neutral-900 dark:text-white">
                {t('home.feature2.title', 'Sicher & Verschlüsselt')}
              </h3>
              <p className="mt-2 text-lg text-neutral-600 dark:text-neutral-400">
                {t('home.feature2.desc', 'Ihre Daten sind mit modernster Verschlüsselung geschützt.')}
              </p>
            </div>

            <div className="text-center group">
              <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mx-auto group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="mt-6 text-xl font-bold text-neutral-900 dark:text-white">
                {t('home.feature3.title', 'KI-gestützt')}
              </h3>
              <p className="mt-2 text-lg text-neutral-600 dark:text-neutral-400">
                {t('home.feature3.desc', 'Automatische Verarbeitung Ihrer Dokumente mit künstlicher Intelligenz.')}
              </p>
            </div>

            <div className="text-center group">
              <div className="flex items-center justify-center h-16 w-16 rounded-xl bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 mx-auto group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="mt-6 text-xl font-bold text-neutral-900 dark:text-white">
                {t('home.feature4.title', 'Keine Kosten & kein Risiko')}
              </h3>
              <p className="mt-2 text-lg text-neutral-600 dark:text-neutral-400">
                {t('home.feature4.desc', 'FWC übernimmt Anspruch und Risiko - Sie sind raus')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Claroclaim Section */}
      <section className="py-16 sm:py-24 bg-white dark:bg-[rgb(var(--background-secondary))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[rgb(var(--foreground))] mb-12">
            {t('home.why_claroclaim.title', 'Warum Claroclaim?')}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* German Mediator */}
            <div className="text-center bg-[rgb(var(--card))] dark:glass rounded-xl p-8 shadow-lg border border-[rgb(var(--border))]">
              <div className="text-5xl mb-4">{t('home.why_claroclaim.german_mediator.icon', '🇩🇪')}</div>
              <h3 className="text-xl font-bold text-[rgb(var(--foreground))] mb-1">
                {t('home.why_claroclaim.german_mediator.title', 'Deutscher Vermittler')}
              </h3>
              <p className="text-lg text-[rgb(var(--foreground-muted))]">
                {t('home.why_claroclaim.german_mediator.desc', 'Claroclaim AG, Frankfurt am Main')}
              </p>
            </div>

            {/* Purchase & Risk Transfer */}
            <div className="text-center bg-[rgb(var(--card))] dark:glass rounded-xl p-8 shadow-lg border border-[rgb(var(--border))]">
              <div className="text-5xl mb-4">{t('home.why_claroclaim.purchase_takeover.icon', '💪')}</div>
              <h3 className="text-xl font-bold text-[rgb(var(--foreground))] mb-1">
                {t('home.why_claroclaim.purchase_takeover.title', 'Ankauf & Risikoübernahme')}
              </h3>
              <p className="text-lg text-[rgb(var(--foreground-muted))]">
                {t('home.why_claroclaim.purchase_takeover.desc', 'Ankauf und Risikoübernahme durch FWC')}
              </p>
            </div>

            {/* Transparency */}
            <div className="text-center bg-[rgb(var(--card))] dark:glass rounded-xl p-8 shadow-lg border border-[rgb(var(--border))]">
              <div className="text-5xl mb-4">{t('home.why_claroclaim.transparency.icon', '🔍')}</div>
              <h3 className="text-xl font-bold text-[rgb(var(--foreground))] mb-1">
                {t('home.why_claroclaim.transparency.title', 'Transparenz')}
              </h3>
              <p className="text-lg text-[rgb(var(--foreground-muted))]">
                {t('home.why_claroclaim.transparency.desc', 'Klare Rollen: Claroclaim ist Servicepartnerin von FWC, FWC erwirbt die Ansprüche')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 sm:py-24 bg-[rgb(var(--background-secondary))] dark:bg-[rgb(var(--background))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block py-2 px-4 rounded-full bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-100 text-base font-medium mb-5 border border-primary-200 dark:border-primary-700">
              {t('home.faq.label', 'HÄUFIG GESTELLTE FRAGEN')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[rgb(var(--foreground))] dark:gradient-text mb-6">
              {t('home.faq.title', 'Häufige Fragen')}
            </h2>
            <p className="mt-4 text-2xl text-[rgb(var(--foreground-muted))] max-w-3xl mx-auto">
              {t('home.faq.subtitle', 'Alles, was Sie über den Amazon-Rechtsfall wissen müssen')}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {[
                {
                  q: t('home.faq.q1', 'Wie viel Ersatz könnte ich erhalten?'),
                  a: t('home.faq.a1', 'Die Höhe des Ersatzes variiert je nach Ihrer Kaufhistorie. Im Durchschnitt können Anspruchsberechtigte mit etwa 15% - 20% des erwarteten Schadens in Bezug auf den berechtigten Umsatz rechnen (Richtwert). Je mehr Sie während des relevanten Zeitraums bei Amazon gekauft haben, desto höher ist Ihr potenzieller Ersatz.')
                },
                {
                  q: t('home.faq.q2', 'Wie lange wird das Gerichtsverfahren dauern?'),
                  a: t('home.faq.a2', 'Rechtsverfahren dieser Größenordnung dauern in der Regel 6-8 Jahre bis zur Lösung. Mit unserem Angebot haben Sie keinen langwierigen Rechtsstreit, sondern erhalten den vereinbarten Betrag ohne juristische Auseinandersetzung mit Amazon.')
                },
                {
                  q: t('home.faq.q3', 'Was ist, wenn ich nicht alle meine Amazon-Belege habe?'),
                  a: t('home.faq.a3', 'Keine Sorge! Wir können Ihnen helfen, Ihre Amazon-Kaufhistorie direkt von Ihrem Amazon-Konto abzurufen.')
                },
                {
                  q: t('home.faq.q4', 'Warum soll ich meinen Anspruch verkaufen anstatt diesen selbst durchzusetzen?'),
                  a: t('home.faq.a4', 'Der Verkauf an FWC gibt Ihnen sofortige Sicherheit: Geld i. d. R. innerhalb von 7 Tagen anstatt jahrelang auf den Ausgang eines gerichtlichen Verfahrens zu warten. Sie vermeiden alle Verfahrenskosten und Risiken, während FWC den gesamten Anspruch und sämtliche Risiken der Durchsetzung übernimmt.')
                },
                {
                  q: t('home.faq.q5', 'Wie sicher ist der Verkauf meines Anspruchs?'),
                  a: t('home.faq.a5', 'FWC ist ein internationaler Finanzinvestor mit langjähriger Erfahrung im Bereich des Anspruchserwerbs und -durchsetzung. Der Kaufvertrag gibt Ihnen Rechtssicherheit und Sie haben ein Widerrufsrecht. Ihre Datensicherheit nehmen wir sehr ernst und halten uns an die geltenden Datenschutzregelungen.')
                },
                {
                  q: t('home.faq.q6', 'Was passiert nach dem Verkauf meines Anspruchs?'),
                  a: t('home.faq.a6', 'Nach dem Verkauf sind Sie komplett raus. FWC übernimmt Ihren Anspruch und kümmert sich um alle weiteren Schritte – von der Prozessführung bis zur Vollstreckung. Sie müssen sich um nichts mehr kümmern und tragen kein Risiko.')
                },
                {
                  q: t('home.faq.q7', 'Kann ich nach dem Verkauf noch selbst gegen Amazon klagen?'),
                  a: t('home.faq.a7', 'Nein, nach dem Verkauf Ihres Anspruchs an FWC übertragen Sie alle Rechte an Ihrem Amazon-Anspruch. FWC wird dann neuer Inhaber der Forderung und führt den gesamten Rechtsprozess.')
                }
              ].map((item, i) => (
                <div 
                  key={i}
                  className="bg-[rgb(var(--card))] dark:glass rounded-xl shadow-lg dark:shadow-2xl border border-[rgb(var(--border))] dark:border-[rgb(var(--border))] overflow-hidden hover:scale-[1.02] transition-transform duration-300"
                >
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-[rgb(var(--foreground))] mb-4">{item.q}</h3>
                    <p className="text-lg text-[rgb(var(--foreground-muted))]">{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-24 relative bg-linear-to-r from-primary-700 to-indigo-900 dark:from-blue-900 dark:to-purple-900 text-white overflow-hidden">
        <div className="absolute top-20 w-full h-20 bg-linear-to-r from-purple-500 to-blue-500 opacity-10 blur-3xl transform -skew-y-6 dark:block hidden"></div>
        <div className="absolute top-40 w-full h-20 bg-linear-to-r from-blue-500 to-purple-500 opacity-10 blur-3xl transform -skew-y-3 dark:block hidden"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
            {t('home.cta.title', 'Verwandeln Sie Ihren Anspruch in bares Geld')}
          </h2>
          <p className="text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
            {t('home.cta.subtitle', 'Während andere noch überlegen, haben unsere Kunden bereits ihr Geld erhalten. Starten Sie jetzt Ihre kostenlose Vorbewertung – es dauert nur wenige Minuten.')}
          </p>
          <button
            onClick={() => {
              trackEvent('bottom_cta_clicked');
              trackClarityEvent('bottom_cta_clicked');
              heroAction.action();
            }}
            className="relative inline-flex items-center px-12 py-6 overflow-hidden text-2xl font-bold text-primary-700 bg-white rounded-xl group shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer"
          >
            <span className="absolute top-0 left-0 w-full h-full bg-linear-to-r from-primary-50 to-white opacity-0 group-hover:opacity-100 transition-all duration-500"></span>
            <span className="relative">{t('home.cta.button', 'Jetzt Anspruch einreichen')}</span>
            <svg className="relative w-6 h-6 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>

        </div>
      </section>
      </main>
    </div>
  );
}